import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import PropTypes from "prop-types";
import * as React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { ManageCrossSkilling } from "../../../../export/cross-skilling";
import { FilterProvider } from "../../../../ts/components/filters/filter-context";
import JobsMatrix from "../../../../ts/components/jobs-matrix";
import PageLayout from "../../../../ts/design/wilya-page-layout";
import { closeSkillInsightDetails } from "../../insight-dashboard/slices/skill-insight-report-slice";
import { closeWorkerInsightDetails } from "../../insight-dashboard/slices/worker-insight-report-slice";
import WorkerInsightReport from "./cross-skilling-dashboard";
function TabPanel(props: any) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role='tabpanel'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box>{children}</Box>}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

const CrossSkillingDashboardTabs = () => {
	const { t } = useTranslation();
	const [value, setValue] = React.useState(0);

	const isSkillInsightsEnabled = useSelector(
		(state: any) => state.user.userData.skillInsightsEnabled
	);

	const isJobsMatrixEnabled = useSelector(
		(state: any) => state.user.userData.jobsMatrixEnabled
	);

	const showCrossSkilling = useSelector(
		(state: any) => state.user.userData.showCrossSkilling
	);

	const currentUserId = useSelector((state: any) => state.user?.userData?.id);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(closeSkillInsightDetails());
		dispatch(closeWorkerInsightDetails());
	}, [dispatch]);

	const handleChange = (event: any, newValue: any) => {
		dispatch(closeSkillInsightDetails());
		dispatch(closeWorkerInsightDetails());

		setValue(newValue);
	};
	// GAT-7289 - when Cross Skilling is off but Jobs Matrix is on, this page
	// shows the Jobs Matrix on its own, with no tab bar.
	if (!showCrossSkilling && isJobsMatrixEnabled)
		return (
			<FilterProvider filterKey={`jobs-matrix-${currentUserId}`}>
				<JobsMatrix />
			</FilterProvider>
		);

	if (!isSkillInsightsEnabled && !isJobsMatrixEnabled)
		return (
			<FilterProvider filterKey={`skill-matrix-${currentUserId}`}>
				<ManageCrossSkilling />
			</FilterProvider>
		);

	const jobsMatrixTabIndex = isSkillInsightsEnabled ? 2 : 1;

	return (
		<Box sx={{ width: "100%", height: "100%", marginTop: -0.9 }}>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }} pl={1}>
				<Tabs
					value={value}
					onChange={handleChange}
					aria-label={t("cross-skilling-dashboard_workers-and-summary-tabs")}
					sx={{
						".MuiTab-root": {
							paddingBottom: 0.8,
						},
					}}
					TabIndicatorProps={{
						sx: {
							height: "1px",
							marginLeft: "15px",
						},
					}}
				>
					<Tab
						label={t("cross-skilling-dashboard_skills-matrix")}
						{...a11yProps(0)}
						sx={{
							fontFamily: "Roboto",
							fontSize: "14px",
							fontWeight: "500",
							lineHeight: "24px",
							letterSpacing: "0.4px",
						}}
					/>
					{isSkillInsightsEnabled && (
						<Tab
							label={t("cross-skilling-dashboard_summary")}
							{...a11yProps(1)}
							sx={{
								fontFamily: "Roboto",
								fontSize: "14px",
								fontWeight: "500",
								lineHeight: "24px",
								letterSpacing: "0.4px",
							}}
						/>
					)}
					{isJobsMatrixEnabled && (
						<Tab
							label={t("cross-skilling-dashboard_jobs-matrix")}
							{...a11yProps(jobsMatrixTabIndex)}
							sx={{
								fontFamily: "Roboto",
								fontSize: "14px",
								fontWeight: "500",
								lineHeight: "24px",
								letterSpacing: "0.4px",
							}}
						/>
					)}
				</Tabs>
			</Box>

			<TabPanel value={value} index={0}>
				<FilterProvider filterKey={`skill-matrix-${currentUserId}`}>
					<ManageCrossSkilling />
				</FilterProvider>
			</TabPanel>
			{isSkillInsightsEnabled && (
				<TabPanel value={value} index={1}>
					<FilterProvider filterKey={`cross-skilling-summary-${currentUserId}`}>
						<WorkerInsightReport />
					</FilterProvider>
				</TabPanel>
			)}
			{isJobsMatrixEnabled && (
				<TabPanel value={value} index={jobsMatrixTabIndex}>
					<FilterProvider filterKey={`jobs-matrix-${currentUserId}`}>
						<JobsMatrix />
					</FilterProvider>
				</TabPanel>
			)}
		</Box>
	);
};

export default CrossSkillingDashboardTabs;
